import React, { useRef, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	List,
	ListItem,
	ListItemText,
	Stack,
	Typography
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const ACCEPT = '.xlsx,.xls';

/**
 * Модалка импорта из Excel: скачать шаблон → выбрать файл → загрузить → отчёт.
 *
 * Импорт частичный: корректные строки сохраняются даже если часть отклонена,
 * поэтому отчёт показываем всегда, а список закрывающий компонент перечитывает
 * при любом imported > 0.
 */
function ExcelImportModal({
	open,
	onClose,
	onImported,
	title,
	hint,
	onDownloadTemplate,
	onUpload,
	templateFileName
}) {
	const { t } = useTranslation();
	const inputRef = useRef(null);

	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [result, setResult] = useState(null);

	const reset = () => {
		setFile(null);
		setResult(null);
		if (inputRef.current) {
			inputRef.current.value = '';
		}
	};

	const handleClose = () => {
		if (uploading) {
			return;
		}
		// Если что-то импортировалось — просим владельца обновить список.
		if (result?.imported > 0) {
			onImported?.();
		}
		reset();
		onClose();
	};

	const handleDownloadTemplate = async () => {
		setDownloading(true);
		try {
			const res = await onDownloadTemplate();
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement('a');
			link.href = url;
			link.download = templateFileName;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			toast.error(t('admin.import.templateError'));
		} finally {
			setDownloading(false);
		}
	};

	const handleUpload = async () => {
		if (!file) {
			return;
		}
		setUploading(true);
		setResult(null);
		try {
			const res = await onUpload(file);
			setResult(res.data);
			if (res.data.imported > 0) {
				toast.success(t('admin.import.success', { count: res.data.imported }));
			}
			if (res.data.imported === 0 && res.data.skipped > 0) {
				toast.warn(t('admin.import.allSkipped'));
			}
		} catch (e) {
			toast.error(e?.response?.data?.message || t('admin.import.uploadError'));
		} finally {
			setUploading(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>

			<DialogContent dividers>
				<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
					{hint}
				</Typography>

				<Button
					variant='outlined'
					startIcon={<DownloadRoundedIcon />}
					onClick={handleDownloadTemplate}
					disabled={downloading}
					sx={{ borderRadius: 2, textTransform: 'none', mb: 2 }}
				>
					{t('admin.import.downloadTemplate')}
				</Button>

				<Divider sx={{ mb: 2 }} />

				<Stack direction='row' spacing={1.5} alignItems='center'>
					<Button
						variant='outlined'
						component='label'
						startIcon={<UploadFileRoundedIcon />}
						disabled={uploading}
						sx={{ borderRadius: 2, textTransform: 'none' }}
					>
						{t('admin.import.chooseFile')}
						<input
							ref={inputRef}
							type='file'
							accept={ACCEPT}
							hidden
							onChange={(e) => {
								setFile(e.target.files?.[0] || null);
								setResult(null);
							}}
						/>
					</Button>
					<Typography variant='body2' color='text.secondary' noWrap>
						{file ? file.name : t('admin.import.noFile')}
					</Typography>
				</Stack>

				{result && (
					<Box sx={{ mt: 2.5 }}>
						<Alert severity={result.skipped > 0 ? 'warning' : 'success'}>
							{t('admin.import.report', {
								imported: result.imported,
								skipped: result.skipped
							})}
						</Alert>

						{result.errors?.length > 0 && (
							<List dense sx={{ maxHeight: 240, overflowY: 'auto', mt: 1 }}>
								{result.errors.map((error) => (
									<ListItem key={error.row} disableGutters>
										<ListItemText
											primary={t('admin.import.rowLabel', { row: error.row })}
											secondary={error.message}
											primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
											secondaryTypographyProps={{ variant: 'body2' }}
										/>
									</ListItem>
								))}
							</List>
						)}
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={handleClose} disabled={uploading} sx={{ textTransform: 'none' }}>
					{t('admin.import.close')}
				</Button>
				<Button
					variant='contained'
					onClick={handleUpload}
					disabled={!file || uploading}
					sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
				>
					{uploading ? t('admin.import.uploading') : t('admin.import.upload')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ExcelImportModal;
